import type { Event } from "../types/Types";

function apiBaseUrl(): string {
  return (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
}

/** Short API share URL so WhatsApp can read event og:image metadata. */
export function getEventInviteUrl(event: Pick<Event, "id">): string {
  const api = apiBaseUrl();
  if (api) {
    return `${api}/Event/${event.id}/share`;
  }
  const url = new URL("/", window.location.origin);
  url.searchParams.set("mode", "register");
  url.searchParams.set("explorer", "1");
  url.searchParams.set("eventId", String(event.id));
  return url.toString();
}

export function getSharedEventQuestionsPath(
  eventId?: string | number | null,
): string | "" {
  const id = String(eventId ?? "").trim();
  if (!id || !/^\d+$/.test(id)) return "";
  return `/event-questions/${id}`;
}

function isShareAbort(err: unknown): boolean {
  return Boolean(
    err &&
      typeof err === "object" &&
      "name" in err &&
      (err as { name?: string }).name === "AbortError",
  );
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to execCommand */
  }
  try {
    const input = document.createElement("textarea");
    input.value = text;
    input.setAttribute("readonly", "");
    input.style.position = "fixed";
    input.style.top = "0";
    input.style.left = "0";
    input.style.opacity = "0";
    document.body.appendChild(input);
    input.focus();
    input.select();
    input.setSelectionRange(0, text.length);
    const copied = document.execCommand("copy");
    document.body.removeChild(input);
    return copied;
  } catch {
    return false;
  }
}

export type EventShareResult = "shared" | "copied" | "aborted" | "failed";

/** iOS Safari rejects share payloads that set both `text` and `url`. */
export async function shareEventInvite(options: {
  title: string;
  url: string;
}): Promise<EventShareResult> {
  const { title, url } = options;
  if (typeof navigator.share === "function") {
    try {
      const payload: ShareData = { title, url };
      if (
        typeof navigator.canShare !== "function" ||
        navigator.canShare(payload)
      ) {
        await navigator.share(payload);
        return "shared";
      }
    } catch (err) {
      if (isShareAbort(err)) return "aborted";
    }
  }

  return (await copyTextToClipboard(url)) ? "copied" : "failed";
}
